import { useCallback, useEffect, useRef, useState } from 'react';
import type { WallScan } from './wall-scan';

/** The photo a climb is drawn on, and where it came from. */
export interface ClimbPhoto {
  /** What the editor draws on — an object URL for a fresh pick, or a stored one. */
  url: string;
  /** The size every mark on it is a fraction of. */
  naturalWidth: number;
  naturalHeight: number;
  /** The file to upload, absent when the photo is one the server already has. */
  file: File | null;
  /** The sector this came from, when it was picked off one. */
  zoneId?: string | null;
  /**
   * The scanned wall this is the photograph of, when it was picked off a scan
   * with holds found on it. Held on the photo rather than beside it because the
   * holds were measured against exactly this picture: they arrive with it and
   * go with it, and a cut of it is another picture that does not keep them.
   */
  scan?: WallScan | null;
}

/** Where a photo the server already holds was picked from. */
export interface PhotoOrigin {
  /** The sector the picture is of. */
  zoneId?: string | null;
  /** The scan the picture is of — see `ClimbPhoto#scan`. */
  scan?: WallScan | null;
}

/**
 * The climb's photo: choosing one, replacing it, taking it away.
 *
 * The natural size is read here rather than left to whoever draws, because it is
 * the frame of reference *every* mark is stored in — a circle's width is a
 * fraction of the photo's width and its height a fraction of its height (see
 * `circularHold`), so nothing can be placed until this is known. A photo whose
 * size has not arrived yet is not offered for drawing at all.
 *
 * Object URLs are revoked as they are replaced: a setter working down a wall
 * goes through a dozen photos in a session, and each one held open is the whole
 * image kept in memory.
 */
export function useClimbPhoto(initialUrl?: string | null) {
  const [photo, setPhoto] = useState<ClimbPhoto | null>(null);
  const [loading, setLoading] = useState(false);
  // The URLs this hook made, so it can give them back. A stored photo's URL
  // belongs to the server and must not be revoked.
  const ownedUrls = useRef<Set<string>>(new Set());

  const release = useCallback((url: string | undefined) => {
    if (!url || !ownedUrls.current.has(url)) return;
    URL.revokeObjectURL(url);
    ownedUrls.current.delete(url);
  }, []);

  // Whether the component is still on screen, as far as its effects can tell.
  const mounted = useRef(true);

  /**
   * Handing back what this hook borrowed, once the component has really gone.
   *
   * Not straight away, and this is the whole of why: React runs mount effects
   * twice in development (StrictMode) — effects, cleanups, effects again — so
   * this cleanup fires while the component is very much alive. A photo chosen
   * *during* that first pass would have its object URL revoked before it had
   * even finished decoding, and a revoked blob URL does not load: the picture
   * came out blank and the editor asked for it again. Which is exactly what
   * happened to a photo handed over from the climb's page (see pending-photo).
   *
   * A tick later there is no doubt: if the effect has run again, the component
   * stayed, and there is nothing to give back.
   */
  useEffect(() => {
    const owned = ownedUrls.current;
    mounted.current = true;
    return () => {
      mounted.current = false;
      setTimeout(() => {
        if (mounted.current) return;
        owned.forEach((url) => URL.revokeObjectURL(url));
        owned.clear();
      }, 0);
    };
  }, []);

  /**
   * Take a file — from the camera, the gallery, or a fetched sector photo.
   * Answers the URL it is drawn from, or null when it would not decode: the
   * page reads the photo for holds once it is in (see `attachScan`).
   */
  const choose = useCallback(
    async (file: File, zoneId?: string | null): Promise<string | null> => {
      setLoading(true);
      const url = URL.createObjectURL(file);
      ownedUrls.current.add(url);

      try {
        const size = await naturalSizeOf(url);
        setPhoto((current) => {
          release(current?.url);
          return { url, file, zoneId: zoneId ?? null, ...size };
        });
        return url;
      } catch {
        // A file that will not decode is not a photo. Leave whatever was there.
        release(url);
        return null;
      } finally {
        setLoading(false);
      }
    },
    [release]
  );

  /**
   * The holds a scan found on the photo now on screen, once they are in — a
   * photo read for them after it went up (`useLiveScan`). Only onto the photo
   * they were measured on: if the setter has moved on to another, they belong
   * to nothing here.
   */
  const attachScan = useCallback((url: string, scan: WallScan) => {
    setPhoto((current) => (current?.url === url ? { ...current, scan } : current));
  }, []);

  /**
   * Take a photo the server already holds — a sector's, or the climb's own.
   *
   * Fetched into a file rather than pointed at: the climb gets its **own** copy
   * of the picture. A sector's photo is one image serving every route on that
   * cliff, and a climb that merely referred to it would lose its drawing the day
   * somebody replaced the sector's shot.
   */
  const chooseFromUrl = useCallback(
    async (url: string, { zoneId = null, scan = null }: PhotoOrigin = {}) => {
      setLoading(true);
      try {
        const blob = await fetch(url).then((response) => response.blob());
        const type = blob.type || 'image/jpeg';
        const file = new File([blob], `climb.${type.includes('png') ? 'png' : 'jpg'}`, { type });
        const objectUrl = URL.createObjectURL(file);
        ownedUrls.current.add(objectUrl);

        const size = await naturalSizeOf(objectUrl);
        setPhoto((current) => {
          release(current?.url);
          return { url: objectUrl, file, zoneId, scan, ...size };
        });
      } catch {
        /* leave whatever was there */
      } finally {
        setLoading(false);
      }
    },
    [release]
  );

  const remove = useCallback(() => {
    setPhoto((current) => {
      release(current?.url);
      return null;
    });
  }, [release]);

  /**
   * Put a cropped version in place of what is there, keeping the source — the
   * sector, but not a scan: its holds were measured on the whole picture, not
   * on this cut of it.
   */
  const replaceWith = useCallback(
    (file: File, width: number, height: number) => {
      const url = URL.createObjectURL(file);
      ownedUrls.current.add(url);
      setPhoto((current) => {
        release(current?.url);
        return { url, file, zoneId: current?.zoneId ?? null, naturalWidth: width, naturalHeight: height };
      });
    },
    [release]
  );

  // The climb being corrected opens on the photo it already has.
  const appliedRef = useRef(false);
  useEffect(() => {
    if (!initialUrl || appliedRef.current) return;
    appliedRef.current = true;
    void chooseFromUrl(initialUrl);
  }, [initialUrl, chooseFromUrl]);

  return { photo, loading, choose, chooseFromUrl, attachScan, replaceWith, remove };
}

/** How big the picture actually is, which is what every mark is measured against. */
function naturalSizeOf(url: string): Promise<{ naturalWidth: number; naturalHeight: number }> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () =>
      resolve({ naturalWidth: image.naturalWidth || 1, naturalHeight: image.naturalHeight || 1 });
    image.onerror = () => reject(new Error('The photo could not be read'));
    image.src = url;
  });
}
